import {Injectable, signal} from '@angular/core';
import {OpenaiResource, PreflightResponse} from '../resource/openai.resource';
import {EMPTY, map, Observable, of, switchMap, tap, throwError} from 'rxjs';
import {HttpEventType} from '@angular/common/http';
import {ExtendMetadataDto} from '../interfaces/extend-metadata.dto';
import {ChatMessage} from '../interfaces/thread-messages.dto';
import {ExtendMetaResponse} from '../interfaces/extend-meta.response';
import {Job} from '../interfaces/jobs.dto';
import {DocumentDraft, OutlineMode} from '../interfaces/document-draft.dto';
import {AIGenerationOptions} from '../../shared/components/ai-generation-bubble/ai-generation-bubble.component';
import {DOCUMENT_MAX_BYTES, DOCUMENT_TOO_LARGE_ERROR_CODE} from '@maporium-workspace/shared';

function documentTooLargeError() {
  return Object.assign(new Error(DOCUMENT_TOO_LARGE_ERROR_CODE), {code: DOCUMENT_TOO_LARGE_ERROR_CODE});
}

@Injectable({ providedIn: 'root' })
export class OpenaiService {
  public extendedMetadataSignal = signal<{ result: ExtendMetadataDto; source: 'node' | 'link' | 'map' } | null>(null);

  constructor(private resource: OpenaiResource) {
  }

  extendMetadata(dto: ExtendMetadataDto, mapId: string,
                 options?: AIGenerationOptions, source: 'node' | 'link' | 'map' = 'node'): Observable<ExtendMetaResponse | null> {
    return this.resource.extendMetadata(dto, mapId, options)
      .pipe(
        tap((r) => this.extendedMetadataSignal.set(r ? {result: r as any, source} : null)),
      );
  }

  generateAnswer(question: string, mapId: string, screenshot?: Blob, mapCenter?: {
    x: number;
    y: number
  }, webSearch?: boolean): Observable<Job> {
    return this.resource.generateAnswer(question, mapId, screenshot, mapCenter, webSearch);
  }

  generateModels(mapId: string): Observable<Job> {
    return this.resource.generateModels(mapId);
  }

  getMessages(threadId: string, limit = 20, after: string): Observable<any> {
    return this.resource.getMessages(threadId, limit, after);
  }

  getMessagesByMap(mapId: string, limit = 20, after?: string): Observable<ChatMessage[]> {
    return this.resource.getMessagesByMap(mapId, limit, after);
  }

  validateOpenAIKey(apiKey: string): Observable<{ valid: boolean; models: any[]; error?: string }> {
    return this.resource.validateOpenAIKey(apiKey);
  }


  describeView(savedViewId: string, mapId: string): Observable<Job> {
    return this.resource.describeView(savedViewId, mapId);
  }

  describeNode(nodeId: string, mapId: string): Observable<Job> {
    return this.resource.describeNode(nodeId, mapId);
  }

  getJobStatus(jobId: string): Observable<Job> {
    return this.resource.getJobStatus(jobId);
  }

  describeLink(id: string, mapId: string) {
    return this.resource.describeLink(id, mapId);
  }

  // Three-step PDF import flow:
  //   1. ask the API for a presigned S3 upload URL, PUT the file directly to S3
  //   2. POST /ai/convert-document/preflight — server extracts text and decides
  //      whether the document fits the AI's context window (no AI call, no credits).
  //      The caller inspects the response and may show a confirmation dialog.
  //   3. POST /ai/convert-document with the extractedKey — returns a Job.
  // Steps 2 and 3 are split so the user can back out of a partial smap before
  // any credits are spent. Direct S3 upload bypasses Heroku's 30s router timeout.
  uploadAndPreflightPdf(file: File): Observable<{ s3Key: string; preflight: PreflightResponse }> {
    // Fail before the presign round-trip; the callers turn this code into the
    // "This PDF file cannot be smapped." notification.
    if (file.size > DOCUMENT_MAX_BYTES) {
      return throwError(() => documentTooLargeError());
    }
    return this.resource.getDocumentUploadUrl(file.name, file.type || undefined).pipe(
      switchMap(({uploadUrl, key, maxBytes}) => {
        if (maxBytes && file.size > maxBytes) {
          return throwError(() => documentTooLargeError());
        }
        return this.resource.uploadToPresignedUrl(uploadUrl, file).pipe(
          switchMap(() => this.resource.preflightDocument(key, file.name)),
          map((preflight) => ({s3Key: key, preflight})),
        );
      }),
    );
  }

  createDocumentDraft(): Observable<{draftId: string}> {
    return this.resource.createDocumentDraft();
  }

  // Registers the document only; the draft page uploads the file and starts indexing.
  addDraftDocument(draftId: string, file: File): Observable<{documentId: string}> {
    if (file.size > DOCUMENT_MAX_BYTES) {
      return throwError(() => documentTooLargeError());
    }
    return this.resource.addDraftDocument(draftId, file.name);
  }

  /** Emits upload progress (0-100), then the ingest job id once the file has landed. */
  uploadDraftDocument(draftId: string, documentId: string, file: File): Observable<{percent: number; jobId?: string}> {
    return this.resource.getDocumentUploadUrl(file.name, file.type || undefined).pipe(
      switchMap(({uploadUrl, key, maxBytes}) => {
        if (maxBytes && file.size > maxBytes) {
          return throwError(() => documentTooLargeError());
        }
        return this.resource.uploadToPresignedUrlWithProgress(uploadUrl, file).pipe(
          switchMap((event) => {
            if (event.type === HttpEventType.UploadProgress) {
              return of({percent: event.total ? Math.round((event.loaded / event.total) * 100) : 0});
            }
            if (event.type === HttpEventType.Response) {
              return this.resource.startDraftIngest(draftId, documentId, key).pipe(map(({jobId}) => ({percent: 100, jobId})));
            }
            return EMPTY;
          }),
        );
      }),
    );
  }

  removeDraftDocument(draftId: string, documentId: string): Observable<void> {
    return this.resource.removeDraftDocument(draftId, documentId);
  }

  getDocumentDraft(id: string): Observable<DocumentDraft> {
    return this.resource.getDocumentDraft(id);
  }

  sendDraftTurn(
    id: string,
    message: string,
    options?: {webSearch?: boolean; includeResources?: boolean; includeImages?: boolean}
  ): Observable<{jobId: string}> {
    return this.resource.sendDraftTurn(id, message, options);
  }

  commitDocumentDraft(id: string, turnId?: string): Observable<{mapId: string}> {
    return this.resource.commitDocumentDraft(id, turnId);
  }

  discardDocumentDraft(id: string): Observable<void> {
    return this.resource.discardDocumentDraft(id);
  }

  reindexDocumentDraft(id: string, mode: OutlineMode): Observable<{draftId: string}> {
    return this.resource.reindexDocumentDraft(id, mode);
  }

  startDocumentConversion(
    s3Key: string,
    originalname: string,
    extractedKey: string,
    additionalInstructions?: string,
  ): Observable<Job> {
    return this.resource.convertDocumentToMap(s3Key, originalname, additionalInstructions, extractedKey);
  }

  generateSmapFromPrompt(prompt: string, webSearch?: boolean, includeResources?: boolean, includeImages?: boolean): Observable<Job> {
    return this.resource.generateSmapFromPrompt(prompt, webSearch, includeResources, includeImages);
  }

  findImage(name: string, description: string, target: 'node' | 'smap'): Observable<{ imageUrl: string | null }> {
    return this.resource.findImage(name, description, target);
  }

  analyzeBottlenecks(mapId: string): Observable<Job> {
    return this.resource.analyzeBottlenecks(mapId);
  }

  optimizeMap(mapId: string): Observable<Job> {
    return this.resource.optimizeMap(mapId);
  }

  generateProposal(mapId: string): Observable<Job> {
    return this.resource.generateProposal(mapId);
  }

  getProposalsForMap(mapId: string): Observable<{id: string; publicSlug: string; createdAt: string}[]> {
    return this.resource.getProposalsForMap(mapId);
  }
}
